'use client';

import { useTranslation } from '@/lib/i18n';
import { CryptoValuationMetric, useCryptoValuation } from '@/hooks/use-crypto-valuation';
import * as echarts from 'echarts';
import { useMemo } from 'react';
import EChart from '@/components/common/EChart';
import { formatCompactValue } from '@/lib/utils/number';

interface CryptoValuationProps {
  symbol: string;
  openInSidebar?: boolean;
}

export default function CryptoValuation(props: CryptoValuationProps) {
  const { symbol, openInSidebar = false } = props;
  const { t } = useTranslation();
  const { data, isLoading } = useCryptoValuation(symbol);

  if (!data?.data?.length && !isLoading) {
    return null;
  }

  return (
    <div>
      <h2 className="py-5 text-lg font-medium">{t('Valuation multiples')}</h2>
      <ValuationChart data={data?.data || []} loading={isLoading} openInSidebar={openInSidebar} />
    </div>
  );
}

function ValuationChart({
  data,
  loading,
  openInSidebar = false,
}: {
  data: CryptoValuationMetric[];
  loading: boolean;
  openInSidebar?: boolean;
}) {
  const { t } = useTranslation();
  const chartData = useMemo(() => [...data].reverse(), [data]);

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (!chartData.length) return null;

    const labels = chartData.map((item) => formatPeriodLabel(item.period_label));
    const fullyDilutedValues = chartData.map((item) => item.pf_fully_diluted);
    const circulatingValues = chartData.map((item) => item.pf_circulating);
    const yAxisBounds = createYAxisBounds([...fullyDilutedValues, ...circulatingValues]);
    const maxFullyDiluted = Math.max(...fullyDilutedValues, 1);
    const scatterData = chartData.map((item, index) => [
      index,
      circulatingValues[index],
      fullyDilutedValues[index],
      item.take_rate,
    ]);

    return {
      backgroundColor: 'transparent',
      grid: {
        top: 28,
        left: 0,
        right: 0,
        bottom: 48,
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(17,17,17,0.95)',
        borderColor: '#303030',
        borderWidth: 1,
        textStyle: { color: '#fff' },
        formatter: (params) => {
          const dataParams = Array.isArray(params) ? params[0] : params;
          const value = Array.isArray(dataParams?.value) ? dataParams.value : [];
          const label = labels[Number(value[0])] || '';
          return [
            `<strong>${label}</strong>`,
            `P/F ${t('Circulating')}: ${formatMultiple(Number(value[1]))}`,
            `P/F ${t('Fully Diluted')}: ${formatMultiple(Number(value[2]))}`,
            `${t('Take Rate')}: ${formatMultiple(Number(value[3]))}`,
          ].join('<br />');
        },
      },
      xAxis: {
        type: 'category',
        data: labels.map((label) => `${label} P/F`),
        boundaryGap: true,
        axisTick: { show: false },
        axisLine: {
          lineStyle: { color: '#242424' },
        },
        axisLabel: {
          color: '#757575',
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#757575',
          fontSize: 12,
          formatter: (value: number) => formatCompactValue(value),
        },
        splitLine: {
          lineStyle: {
            color: '#242424',
            width: 1,
          },
        },
      },
      series: [
        {
          name: 'P/F',
          type: 'scatter',
          data: scatterData,
          symbolSize: (value) => {
            const fullyDiluted = Array.isArray(value) ? Number(value[2]) : 0;
            return 14 + (fullyDiluted / maxFullyDiluted) * 16;
          },
          itemStyle: {
            color: '#8B3DFF',
            opacity: 1,
          },
          emphasis: {
            scale: 1.08,
            itemStyle: {
              color: '#A35BFF',
            },
          },
        },
      ],
    };
  }, [chartData, t]);

  return (
    <div className="relative h-[320px] w-full bg-black">
      <EChart
        option={option}
        loading={loading}
        empty={!chartData.length}
        className="size-full"
        wrapperClassName="size-full"
        openInSidebar={openInSidebar}
        loadingFallback={<div className="absolute inset-0 animate-pulse rounded-lg bg-[#111]" />}
        emptyFallback={
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#757575]">
            {t('No valuation data')}
          </div>
        }
      />
    </div>
  );
}

function formatPeriodLabel(periodLabel: string) {
  return periodLabel.replace(/\b(\d{4})\b/g, (_, year) => `FY${String(year).slice(-2)}`);
}

function formatMultiple(value: number) {
  if (!Number.isFinite(value)) return '--';
  return `${trimFixed(value, 2)}x`;
}

function createYAxisBounds(values: number[]) {
  const finiteValues = values.filter(Number.isFinite);

  if (!finiteValues.length) {
    return { min: 0, max: 15 };
  }

  const minValue = Math.min(...finiteValues);
  const maxValue = Math.max(...finiteValues);
  const range = Math.max(maxValue - minValue, Math.abs(maxValue) * 0.18, 1);
  const padding = range * 0.18;
  const min = Math.max(0, Math.floor((minValue - padding) / 5) * 5);
  const max = Math.max(15, Math.ceil((maxValue + padding) / 5) * 5);

  return { min, max };
}

function trimFixed(value: number, decimals: number) {
  return value.toFixed(decimals).replace(/\.?0+$/, '');
}
